/**
 * `github` — issues and comments of a repository (sources.md "github").
 *
 * Optional device token `github:token`: with it every request carries `Authorization: Bearer` and is
 * `direct-only` (credentials never pass through the relay); without it the API is reached directly and
 * unauthenticated (CORS `*`). Cursor: `{ issuesAfter, commentsAfter, etags: { issues, comments }, titles }`
 * — `titles` caches the title (and pull-request flag) of up to 200 issues, for comment titles.
 */

import type { FetchRequest, FetchResponse } from '../contracts/platform.js';
import type { RawItem, SourceClient, SourceCursor, SourceFetchContext, SourceFetchResult, SourceRuntime } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { isRecord } from './feed.js';
import { assertOk, invalidInput, newestFirst, parseDate, parseHttpUrl, parseJson, parseRetryAfter, rateLimited } from './http.js';

export const GITHUB_API = 'https://api.github.com';
const PER_PAGE = 50;
/** sources.md: cached issue titles in the cursor, max 200. */
const TITLE_CACHE_MAX = 200;

const NAME = /^[A-Za-z0-9_.-]+$/;

export function parseGithubAddress(address: string): { owner: string; repo: string } | null {
  const raw = address.trim().replace(/\.git$/, '');
  const short = /^([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)$/.exec(raw);
  if (short?.[1] && short[2]) return { owner: short[1], repo: short[2] };
  const url = parseHttpUrl(raw);
  if (!url || (url.hostname !== 'github.com' && url.hostname !== 'www.github.com')) return null;
  const [owner, repo] = url.pathname.split('/').filter((p) => p !== '');
  if (!owner || !repo || !NAME.test(owner) || !NAME.test(repo.replace(/\.git$/, ''))) return null;
  return { owner, repo: repo.replace(/\.git$/, '') };
}

interface TitleEntry {
  t: string;
  pr: boolean;
}

interface GithubCursor {
  issuesAfter: string | null;
  commentsAfter: string | null;
  etags: { issues: string | null; comments: string | null };
  titles: Record<string, TitleEntry>;
}

function readCursor(cursor: SourceCursor | null): GithubCursor {
  const c = cursor ?? {};
  const etags = isRecord(c.etags) ? c.etags : {};
  const titles: Record<string, TitleEntry> = {};
  if (isRecord(c.titles)) {
    for (const [n, v] of Object.entries(c.titles)) {
      if (isRecord(v) && typeof v.t === 'string') titles[n] = { t: v.t, pr: v.pr === true };
    }
  }
  return {
    issuesAfter: typeof c.issuesAfter === 'string' ? c.issuesAfter : null,
    commentsAfter: typeof c.commentsAfter === 'string' ? c.commentsAfter : null,
    etags: { issues: typeof etags.issues === 'string' ? etags.issues : null, comments: typeof etags.comments === 'string' ? etags.comments : null },
    titles,
  };
}

function later(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return (parseDate(b) ?? 0) > (parseDate(a) ?? 0) ? b : a;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const login = (v: unknown): string | undefined => (isRecord(v) && typeof v.login === 'string' ? v.login : undefined);

interface Requester {
  get(url: string, etag: string | null): Promise<FetchResponse>;
}

function requester(runtime: SourceRuntime, token: string | null, what: string): Requester {
  return {
    async get(url, etag) {
      const headers: Record<string, string> = { Accept: 'application/vnd.github.full+json', 'X-GitHub-Api-Version': '2022-11-28' };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (etag) headers['If-None-Match'] = etag;
      const request: FetchRequest = { url, method: 'GET', headers, route: token ? 'direct-only' : 'auto' };
      const response = await runtime.fetcher.fetch(request);
      const now = runtime.clock.now();
      // An exhausted rate limit is a 403 (or 429) with x-ratelimit-remaining: 0 — not a refused token.
      if ((response.status === 403 || response.status === 429) && response.headers['x-ratelimit-remaining'] === '0') {
        const reset = Number(response.headers['x-ratelimit-reset']);
        const retry = parseRetryAfter(response.headers['retry-after'], now) ?? (Number.isFinite(reset) ? Math.max(0, reset * 1000 - now) : null);
        throw rateLimited(`GitHub's rate limit is exhausted${token ? '' : ' (add a token for 5,000 requests an hour)'}.`, retry, { status: response.status });
      }
      assertOk(response, { credential: token !== null, what, now });
      return response;
    },
  };
}

function issueItem(issue: Record<string, unknown>): RawItem | null {
  const number = issue.number;
  if (typeof number !== 'number') return null;
  const isPull = isRecord(issue.pull_request);
  const item: RawItem = {
    externalId: `issue:${number}`,
    title: `#${number} ${str(issue.title) ?? ''}`.trim(),
    threadKey: String(number),
    extra: {
      number,
      state: str(issue.state),
      labels: Array.isArray(issue.labels) ? issue.labels.map((l) => (isRecord(l) ? str(l.name) : str(l))).filter((l): l is string => l !== null) : [],
      isPull,
    },
  };
  const author = login(issue.user);
  if (author) item.author = author;
  const publishedAt = parseDate(str(issue.created_at));
  if (publishedAt !== undefined) item.publishedAt = publishedAt;
  const html = str(issue.body_html);
  if (html) item.html = html;
  const url = str(issue.html_url);
  if (url) item.url = url;
  return item;
}

function issueNumberOf(comment: Record<string, unknown>): number | null {
  const m = /\/issues\/(\d+)$/.exec(str(comment.issue_url) ?? '');
  return m?.[1] ? Number(m[1]) : null;
}

function asArray(value: unknown, what: string): Record<string, unknown>[] {
  if (!Array.isArray(value)) throw new DigesterError('parse_failed', `${what} did not return a list.`);
  return value.filter(isRecord);
}

export const githubClient: SourceClient<'github'> = {
  kind: 'github',
  defaultPullMinutes: 30,

  async resolve(input) {
    const parsed = input.config?.owner && input.config.repo ? { owner: input.config.owner, repo: input.config.repo } : parseGithubAddress(input.address);
    if (!parsed) throw invalidInput('Enter owner/repo or a GitHub repository URL.');
    return {
      kind: 'github',
      locator: `github:${parsed.owner}/${parsed.repo}`.toLowerCase(),
      name: `${parsed.owner}/${parsed.repo}`,
      config: {
        kind: 'github',
        owner: parsed.owner,
        repo: parsed.repo,
        issues: input.config?.issues ?? true,
        comments: input.config?.comments ?? true,
        pullRequests: input.config?.pullRequests ?? false,
      },
      requiredSecrets: [],
    };
  },

  async fetch(ctx: SourceFetchContext<'github'>): Promise<SourceFetchResult> {
    const { owner, repo, issues: wantIssues, comments: wantComments, pullRequests } = ctx.source.config;
    const token = await ctx.runtime.secrets.get('github:token');
    const gh = requester(ctx.runtime, token, `GitHub ${owner}/${repo}`);
    const base = `${GITHUB_API}/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`;
    const cursor = readCursor(ctx.cursor);
    const next: GithubCursor = { ...cursor, etags: { ...cursor.etags }, titles: { ...cursor.titles } };
    const items: RawItem[] = [];
    let notModified = true;

    if (wantIssues) {
      const response = await gh.get(`${base}/issues?state=all&sort=created&direction=desc&per_page=${PER_PAGE}`, cursor.etags.issues);
      if (response.status !== 304) {
        notModified = false;
        next.etags.issues = response.headers['etag'] ?? null;
        const after = parseDate(cursor.issuesAfter);
        for (const issue of asArray(parseJson(response.text, 'GitHub issues'), 'GitHub issues')) {
          const item = issueItem(issue);
          if (!item) continue;
          const isPull = isRecord(issue.pull_request);
          next.titles[String(issue.number)] = { t: str(issue.title) ?? '', pr: isPull };
          if (isPull && !pullRequests) continue;
          if (after !== undefined && (item.publishedAt ?? 0) <= after) continue;
          items.push(item);
          next.issuesAfter = later(next.issuesAfter, str(issue.created_at));
        }
      }
    }

    if (wantComments) {
      const since = cursor.commentsAfter ? `&since=${encodeURIComponent(cursor.commentsAfter)}` : '';
      const response = await gh.get(`${base}/issues/comments?sort=created&direction=desc${since}&per_page=${PER_PAGE}`, cursor.etags.comments);
      if (response.status !== 304) {
        notModified = false;
        next.etags.comments = response.headers['etag'] ?? null;
        const after = parseDate(cursor.commentsAfter);
        for (const comment of asArray(parseJson(response.text, 'GitHub comments'), 'GitHub comments')) {
          const number = issueNumberOf(comment);
          const created = str(comment.created_at);
          const id = comment.id;
          if (number === null || typeof id !== 'number') continue;
          if (after !== undefined && (parseDate(created) ?? 0) <= after) continue;
          let entry = next.titles[String(number)];
          if (!entry) {
            // One extra issue fetch, then cached in the cursor.
            const r = await gh.get(`${base}/issues/${number}`, null);
            const issue = parseJson(r.text, 'GitHub issue');
            entry = isRecord(issue) ? { t: str(issue.title) ?? '', pr: isRecord(issue.pull_request) } : { t: '', pr: false };
            next.titles[String(number)] = entry;
          }
          next.commentsAfter = later(next.commentsAfter, created);
          if (entry.pr && !pullRequests) continue;
          const item: RawItem = {
            externalId: `comment:${id}`,
            title: `Re: #${number} ${entry.t}`.trim(),
            threadKey: String(number),
            extra: { number, commentId: id },
          };
          const author = login(comment.user);
          if (author) item.author = author;
          const publishedAt = parseDate(created);
          if (publishedAt !== undefined) item.publishedAt = publishedAt;
          const html = str(comment.body_html);
          if (html) item.html = html;
          const url = str(comment.html_url);
          if (url) item.url = url;
          items.push(item);
        }
      }
    }

    // Keep the title cache bounded (most recently added last).
    const keys = Object.keys(next.titles);
    if (keys.length > TITLE_CACHE_MAX) {
      for (const k of keys.slice(0, keys.length - TITLE_CACHE_MAX)) delete next.titles[k];
    }
    const out: SourceCursor = { issuesAfter: next.issuesAfter, commentsAfter: next.commentsAfter, etags: next.etags, titles: next.titles };
    if (notModified && (wantIssues || wantComments)) return { items: [], cursor: out, notModified: true };
    return { items: newestFirst(items, ctx.maxItems), cursor: out };
  },
};
